<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { computed } from "vue";
import {
  SatProductDetails,
  useSatProductDetails,
} from "@chatfood/design-system";
import type { IItemDetailsProps } from "./props";
import { useLocale } from "~/composables/use-locale";
import type { IDomainItem } from "~/domain/item";
import type { IDomainServingMeasurement } from "#imports";

const css = bemBuilder("item-details");

const props = defineProps<IItemDetailsProps>();

const { t } = useLocale();

const productFormatted = computed(() => formatProduct(props.item));

const modifiersFormatted = computed(() =>
  props.item.modifierLists.flatMap((modifierList) =>
    modifierList.modifiers.map((modifier) => formatProduct(modifier)),
  ),
);

const getCaloriesLabel = (calories?: number, highCalories?: number) => {
  if (!calories) return;

  if (highCalories) {
    return t("components.item_details.range_calories_value", {
      calories,
      highCalories,
    });
  }

  return t("components.item_details.calories_value", { calories });
};

const formatProduct = (item: IDomainItem) => ({
  id: item.id,
  name: item.name,
  description: item.description,
  calories: getCaloriesLabel(item.calories, item.caloriesRangeHigh),
  allergens: item.allergens,
  nutritional: {
    fat: getNumberString(item?.nutritional?.fat),
    sugar: getNumberString(item?.nutritional?.sugar),
    saturatedFat: getNumberString(item?.nutritional?.saturatedFat),
    carbohydrates: getNumberString(item?.nutritional?.carbohydrates),
    protein: getNumberString(item?.nutritional?.protein),
    salt: getNumberString(item?.nutritional?.salt),
  },
  servingSize: getServingMeasurementLabel(
    "servingSize",
    item?.nutritional?.servingSize,
  ),
  netQuantity: getServingMeasurementLabel(
    "netQuantity",
    item?.nutritional?.netQuantity,
  ),
  beverage: {
    alcohol: item?.beverage?.alcohol
      ? t("components.item_details.alcohol", { amount: item.beverage.alcohol })
      : undefined,
    caffeine: item?.beverage?.caffeine
      ? t("components.item_details.caffeine", {
          amount: item.beverage.caffeine,
        })
      : undefined,
  },
  reusable: getBooleanLabel(
    t("components.item_details.reusable_label_yes"),
    t("components.item_details.reusable_label_no"),
    item?.packaging?.reusable,
  ),
  storageInstructions: item?.packaging?.storageInstructions,
  legalName: item?.supplemental?.legalName,
  instructionsForUse: item?.supplemental?.instructionsForUse,
  ingredients: item?.supplemental?.ingredients ?? [],
  additives: item?.supplemental?.additives ?? [],
  prepackaged: getBooleanLabel(
    t("components.item_details.prepackaged_label_yes"),
    t("components.item_details.prepackaged_label_no"),
    item?.supplemental?.prepackaged,
  ),
  foodBusinessOperator: {
    name: item?.supplemental?.foodBusinessOperator?.name,
    address: item?.supplemental?.foodBusinessOperator?.address,
    countryOfOrigin: item?.supplemental?.foodBusinessOperator?.countryOfOrigin,
  },
});

const getNumberString = (amount?: number) => {
  if (typeof amount !== "number") return;

  return t("components.item_details.nutrition_table.grams_label", {
    amount,
  });
};

const getBooleanLabel = (
  validText: string,
  inValidText: string,
  state?: boolean,
) => {
  if (state === undefined) return;

  return state ? validText : inValidText;
};

const getServingMeasurementLabel = (
  key: "servingSize" | "netQuantity",
  obj?: IDomainServingMeasurement,
) => {
  if (!obj) return;

  const params = {
    amount: obj?.amount ?? "",
    count: obj?.countUnitDescription ?? "",
    type: obj?.unitType ?? "",
  };

  return key === "servingSize"
    ? t("components.item_details.nutrition_table.serving_size", params)
    : t("components.item_details.nutrition_table.net_quantity", params);
};

const { product, modifiers } = useSatProductDetails({
  product: productFormatted.value,
  modifiers: modifiersFormatted.value,
  content: {
    caloriesLabel: `${t("components.item_details.nutrition_label")} `,
    nutritionTitle: t("components.item_details.nutrition_table.title"),
    emptyMessageLabel: t("components.item_details.empty_label"),
    allergens: t("components.item_details.allergens_label"),
    nutritional: {
      fat: t("components.item_details.nutrition_table.fat"),
      sugar: t("components.item_details.nutrition_table.sugar"),
      saturatedFat: t("components.item_details.nutrition_table.saturated_fat"),
      carbohydrates: t("components.item_details.nutrition_table.carbohydrates"),
      protein: t("components.item_details.nutrition_table.protein"),
      salt: t("components.item_details.nutrition_table.salt"),
    },
    beverage: t("components.item_details.beverage_label"),
    reusable: t("components.item_details.reusable_label"),
    storageInstructions: t(
      "components.item_details.storage_instructions_label",
    ),
    legalName: t("components.item_details.legal_name_label"),
    instructionsForUse: t("components.item_details.instructions_to_use_label"),
    ingredients: t("components.item_details.ingredients_label"),
    additives: t("components.item_details.additives_label"),
    prepackaged: t("components.item_details.prepackaged_label"),
    foodBusinessOperator: t("components.item_details.business_operator_label"),
  },
});
</script>

<template>
  <div :class="css()">
    <SatProductDetails :product="product" :modifiers="modifiers" />
  </div>
</template>

<style lang="scss" scoped>
.item-details {
  position: relative;
}
</style>
